import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const CATEGORIES = ["Bagno", "Cucina", "Borracce", "Auto & Viaggi", "Casa & Ufficio"];

const EMPTY_FORM = {
  name: "",
  category: "Cucina",
  price: "",
  stock: 5,
  description: "",
  material: "",
  dimensions: "",
  image: "",
  featured: false,
};

export default function Admin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [adminLogin, setAdminLogin] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [nlForm, setNlForm] = useState({ subject: "", message: "" });
  const [nlBusy, setNlBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const genDescription = async () => {
    if (!form.name) {
      toast.error("Inserisci prima il nome del prodotto");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/products/generate-description", {
        name: form.name,
        category: form.category,
        material: form.material,
        dimensions: form.dimensions,
      });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("Descrizione generata con AI");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/newsletter/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  const submitAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginBusy(true);
    try {
      const data = await login(adminLogin.email, adminLogin.password);
      if (data.role !== "admin") setLoginError("Questo account non è amministratore.");
    } catch (err) {
      setLoginError(formatApiError(err));
    } finally {
      setLoginBusy(false);
    }
  };

  if (user === null) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-muted-foreground">Verifica accesso…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm border border-white/10 bg-card p-10">
          <p className="overline-label mb-4">Area riservata</p>
          <h1 className="font-serif font-light text-3xl mb-2">Accesso amministratore</h1>
          <p className="text-muted-foreground text-sm mb-8">Pannello di gestione AB fëlise shop</p>
          <form onSubmit={submitAdminLogin} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-login-email"
                type="email"
                required
                value={adminLogin.email}
                onChange={(e) => setAdminLogin({ ...adminLogin, email: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
              <Input
                id="admin-password"
                data-testid="admin-login-password"
                type="password"
                required
                value={adminLogin.password}
                onChange={(e) => setAdminLogin({ ...adminLogin, password: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            {loginError && <p className="text-sm text-destructive" data-testid="admin-login-error">{loginError}</p>}
            <button
              type="submit"
              disabled={loginBusy}
              data-testid="admin-login-submit"
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {loginBusy ? "Accesso…" : "Entra nel pannello"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  if (user.role !== "admin") {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="admin-denied">
        <h1 className="font-serif font-light text-4xl mb-6">Accesso riservato</h1>
        <p className="text-muted-foreground mb-8">Quest'area è dedicata all'amministratore.</p>
        <button
          onClick={logout}
          data-testid="admin-denied-logout"
          className="text-primary text-sm tracking-wide"
        >
          Esci e riprova con l'account amministratore
        </button>
      </main>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price: String(p.price) });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/products", payload);
        toast.success("Prodotto creato");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const adjustStock = async (p, delta) => {
    try {
      const next = Math.max(0, (p.stock ?? 0) + delta);
      await api.patch(`/products/${p.id}/stock`, { stock: next });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendNewsletter = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Inviare la newsletter a ${subscribers.length} iscritti?`)) return;
    setNlBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", nlForm);
      toast.success(
        `Newsletter inviata a ${data.sent} iscritti${data.failed ? ` (${data.failed} non riusciti)` : ""}`
      );
      setNlForm({ subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNlBusy(false);
    }
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline-label mb-4">Area riservata — gestione</p>
            <h1 className="font-serif font-light text-4xl md:text-6xl">
              Pannello <span className="italic text-primary">admin</span>
            </h1>
          </div>
          <button
            data-testid="admin-add-product-btn"
            onClick={openCreate}
            className="inline-flex items-center gap-3 h-13 px-8 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
          >
            <Plus size={15} /> Nuovo prodotto
          </button>
        </div>

        <div className="border border-white/10 bg-card" data-testid="admin-products-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Prodotto</TableHead>
                <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Categoria</TableHead>
                <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Prezzo</TableHead>
                <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Giacenza</TableHead>
                <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">In evidenza</TableHead>
                <TableHead className="text-right text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} className="border-white/10" data-testid={`admin-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-13 overflow-hidden flex-shrink-0">
                        <img src={p.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <span className="font-serif text-lg">{p.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{p.category}</TableCell>
                  <TableCell className="text-sm">{eur(p.price)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2" data-testid={`stock-cell-${p.id}`}>
                      <button
                        data-testid={`stock-minus-${p.id}`}
                        onClick={() => adjustStock(p, -1)}
                        className="h-7 w-7 border border-white/15 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-sm"
                        aria-label="Riduci giacenza"
                      >
                        −
                      </button>
                      <span className={`text-sm w-8 text-center ${(p.stock ?? 0) <= 0 ? "text-destructive" : ""}`}>
                        {p.stock ?? 0}
                      </span>
                      <button
                        data-testid={`stock-plus-${p.id}`}
                        onClick={() => adjustStock(p, 1)}
                        className="h-7 w-7 border border-white/15 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-sm"
                        aria-label="Aumenta giacenza"
                      >
                        +
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{p.featured ? "Sì" : "—"}</TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`admin-edit-${p.id}`}
                      onClick={() => openEdit(p)}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors"
                      aria-label={`Modifica ${p.name}`}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      data-testid={`admin-delete-${p.id}`}
                      onClick={() => remove(p)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Elimina ${p.name}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <h2 className="font-serif font-light text-3xl mt-20 mb-8">
          Ordini <span className="text-primary">({orders.length})</span>
        </h2>
        {orders.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="admin-no-orders">
            Nessun ordine ancora.
          </p>
        ) : (
          <div className="border border-white/10 bg-card" data-testid="admin-orders-table">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Cliente</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Pezzi</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Totale</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Stato</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-[11px] tracking-[0.2em]">Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} className="border-white/10 cursor-pointer hover:bg-white/5 transition-colors" data-testid={`admin-order-${o.id}`} onClick={() => navigate(`/admin/ordini/${o.id}`)}>
                    <TableCell className="text-sm">{o.customer?.email || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                    </TableCell>
                    <TableCell className="text-sm">{eur(o.total)}</TableCell>
                    <TableCell>
                      <span
                        className={`text-[11px] uppercase tracking-[0.2em] ${
                          o.payment_status === "paid" ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {o.payment_status === "paid" ? "Pagato" : o.payment_status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT") : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <h2 className="font-serif font-light text-3xl mt-20 mb-8">
          Newsletter <span className="text-primary">({subscribers.length} iscritti)</span>
        </h2>
        <div className="grid lg:grid-cols-2 gap-8 items-start" data-testid="newsletter-section">
          <div className="border border-white/10 bg-card p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">Iscritti</p>
            {subscribers.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="newsletter-empty">
                Nessun iscritto ancora.
              </p>
            ) : (
              <ul className="space-y-3 max-h-72 overflow-y-auto" data-testid="newsletter-list">
                {subscribers.map((s) => (
                  <li key={s.email} className="text-sm flex justify-between gap-4 border-b border-white/5 pb-2">
                    <span>{s.name} {s.surname || ""}</span>
                    <span className="text-muted-foreground">{s.email}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <form onSubmit={sendNewsletter} className="border border-white/10 bg-card p-8 space-y-5" data-testid="newsletter-form">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Nuovo invio — da ordini@abfeliseshop.it
            </p>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Oggetto</Label>
              <Input
                data-testid="newsletter-subject"
                required
                maxLength={200}
                value={nlForm.subject}
                onChange={(e) => setNlForm({ ...nlForm, subject: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Messaggio</Label>
              <Textarea
                data-testid="newsletter-message"
                required
                maxLength={5000}
                value={nlForm.message}
                onChange={(e) => setNlForm({ ...nlForm, message: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary min-h-[140px]"
              />
            </div>
            <button
              type="submit"
              disabled={nlBusy || subscribers.length === 0}
              data-testid="newsletter-send"
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {nlBusy ? "Invio in corso…" : "Invia newsletter"}
            </button>
          </form>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif font-light text-3xl">
              {editing ? "Modifica prodotto" : "Nuovo prodotto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5 mt-4" data-testid="product-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nome</Label>
              <Input
                data-testid="product-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Prezzo (€)</Label>
                <Input
                  data-testid="product-form-price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="bg-secondary border-white/10 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Giacenza (pezzi)</Label>
                <Input
                  data-testid="product-form-stock"
                  type="number"
                  min="0"
                  required
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                  className="bg-secondary border-white/10 focus-visible:ring-primary"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-secondary border-white/10" data-testid="product-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10 text-foreground">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="focus:bg-white/5 focus:text-foreground">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">URL immagine</Label>
              <Input
                data-testid="product-form-image"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Materiale</Label>
                <Input
                  data-testid="product-form-material"
                  value={form.material}
                  onChange={(e) => setForm({ ...form, material: e.target.value })}
                  className="bg-secondary border-white/10 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Dimensioni</Label>
                <Input
                  data-testid="product-form-dimensions"
                  value={form.dimensions}
                  onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                  className="bg-secondary border-white/10 focus-visible:ring-primary"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Descrizione</Label>
                <button
                  type="button"
                  onClick={genDescription}
                  disabled={aiBusy}
                  data-testid="ai-generate-description"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <Sparkles size={13} /> {aiBusy ? "Generazione…" : "Genera con AI"}
                </button>
              </div>
              <Textarea
                data-testid="product-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-secondary border-white/10 focus-visible:ring-primary min-h-[100px]"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="product-form-featured-label">
              <Checkbox
                data-testid="product-form-featured"
                checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: !!v })}
                className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground">Metti in evidenza in home</span>
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="product-form-submit"
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Salvataggio…" : editing ? "Salva modifiche" : "Crea prodotto"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
